/**
 * BotActivationBanner — prompts the user to press Start in the bot (дельта §1).
 *
 * The bot cannot DM a user who has never started it, so order/status notifications would
 * silently never arrive. When ``me.bot_started`` is ``false`` we surface a prominent strip
 * with an «Активировать бота» action that opens ``t.me/<bot>?start=activate`` inside
 * Telegram (which STARTs the bot and lets it message the user). When the bot is already
 * started — or we cannot build a valid link — the banner renders nothing.
 */
import { botStartLink, openTelegramLink } from "@melusine/shared-telegram";
import type { Me } from "@entities/user";

import styles from "./BotActivationBanner.module.css";

export interface BotActivationBannerProps {
  readonly me: Pick<Me, "bot_started" | "bot_username">;
  /** Deep-link payload passed to the bot's ``/start`` (defaults to ``activate``). */
  readonly startParam?: string;
}

const BellIcon = (): JSX.Element => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.7 21a2 2 0 0 1-3.4 0" />
  </svg>
);

export function BotActivationBanner({
  me,
  startParam = "activate",
}: BotActivationBannerProps): JSX.Element | null {
  if (me.bot_started) {
    return null;
  }
  const link = botStartLink(me.bot_username, startParam);
  if (link === null) {
    return null;
  }

  return (
    <section className={styles.banner} role="status">
      <span className={styles.icon} aria-hidden="true">
        <BellIcon />
      </span>
      <div className={styles.text}>
        <span className={styles.title}>Уведомления выключены</span>
        <span className={styles.subtitle}>
          Для получения уведомлений активируйте бота Melusine Shop
        </span>
      </div>
      <button
        type="button"
        className={styles.action}
        onClick={() => {
          openTelegramLink(link);
        }}
      >
        Активировать бота
      </button>
    </section>
  );
}
